import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type { ReportSection } from '@/utils/reportSections';

interface ReportSectionEditorProps {
  sections: ReportSection[];
  onChange: (next: ReportSection[]) => void;
  highlightIndex?: number | null;
  disabled?: boolean;
}

// Default suggestions shown as placeholder text in new section heading inputs
// — these mirror the categories the agents are expected to cover in a report.
const HEADING_SUGGESTIONS = [
  'Social Media Review',
  'Financial & Credit Indicators',
  'Personal Identification Verification',
  'Workplace Conduct',
  'Criminal & Law Enforcement',
  'Litigation History',
  'Professional Discipline',
  'Reputational Review',
];

export default function ReportSectionEditor({
  sections,
  onChange,
  highlightIndex = null,
  disabled = false,
}: ReportSectionEditorProps) {
  const update = (index: number, patch: Partial<ReportSection>) => {
    const next = sections.map((s, i) => (i === index ? { ...s, ...patch } : s));
    onChange(next);
  };

  const remove = (index: number) => {
    onChange(sections.filter((_, i) => i !== index));
  };

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    onChange(next);
  };

  const add = () => {
    onChange([...sections, { heading: '', body: '' }]);
  };

  return (
    <div className="flex flex-col gap-3">
      {sections.length === 0 && (
        <p className="text-xs text-[#717680] italic">
          No sections yet. Add findings one at a time — each section gets its own heading and body.
        </p>
      )}

      {sections.map((section, index) => {
        const number = index + 1;
        const isHighlighted = highlightIndex === index;
        const placeholder = HEADING_SUGGESTIONS[index] ?? 'Section heading';
        return (
          <div
            key={index}
            className={`rounded-lg border bg-white transition-colors ${
              isHighlighted
                ? 'border-red-300 ring-2 ring-red-100'
                : 'border-[#e9eaeb] hover:border-[#d5d7da]'
            }`}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#f0f0f0] bg-[#fafafa] rounded-t-lg">
              <span className="text-xs font-semibold text-[#717680]">
                Section {number}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={disabled || index === 0}
                  className="p-1 rounded text-[#717680] hover:bg-white hover:text-[#1b1b1b] disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={disabled || index === sections.length - 1}
                  className="p-1 rounded text-[#717680] hover:bg-white hover:text-[#1b1b1b] disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  disabled={disabled}
                  className="p-1 rounded text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove section"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2 px-3 py-3">
              <input
                type="text"
                value={section.heading}
                onChange={(e) => update(index, { heading: e.target.value })}
                disabled={disabled}
                placeholder={placeholder}
                className="w-full bg-white border border-[#d5d7da] rounded-md h-10 px-3 text-sm font-semibold text-[#181d27] placeholder:text-[#9aa0a6] placeholder:font-normal outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 transition-colors disabled:bg-[#fafafa]"
              />
              <textarea
                value={section.body}
                onChange={(e) => update(index, { body: e.target.value })}
                disabled={disabled}
                rows={5}
                placeholder="Describe what was verified, the source, and the finding."
                className="w-full bg-white border border-[#d5d7da] rounded-md px-3 py-2 text-sm text-[#181d27] placeholder:text-[#9aa0a6] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 transition-colors resize-y min-h-[100px] disabled:bg-[#fafafa]"
              />
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={add}
        disabled={disabled}
        className="self-start inline-flex items-center gap-2 px-4 py-2 border border-dashed border-[#d5d7da] rounded-lg text-sm font-medium text-[#1b1b1b] hover:bg-[#fafafa] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Plus className="w-4 h-4" />
        Add section
      </button>
    </div>
  );
}
